import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import {
  Database,
  Download,
  Upload,
  Cloud,
  HardDrive,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  Lock,
} from 'lucide-react'
import {
  downloadBackupJSON,
  downloadBackupExcel,
  uploadBackupToGoogleDrive,
  uploadBackupToDropbox,
  uploadBackupToS3,
  getBackupRecords,
  generateBackupData,
} from '../services/backupService'
import { getBackupConfiguration, triggerManualBackup } from '../services/backup'
import { encryptBackupData, decryptBackupData, generateEncryptionKey, storeEncryptionKey, getEncryptionKey } from '../services/backupEncryption'
import { formatDate } from '../utils/helpers'
import { useToast } from '../contexts/ToastContext'

const BackupRestore = () => {
  const [backupConfig, setBackupConfig] = useState(null)
  const [backupRecords, setBackupRecords] = useState([])
  const [loading, setLoading] = useState(true)
  const [backingUp, setBackingUp] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [backupType, setBackupType] = useState('json') // 'json', 'excel'
  const [cloudService, setCloudService] = useState('local') // 'local', 'google_drive', 'dropbox', 's3'
  const [includeAttachments, setIncludeAttachments] = useState(true)
  const [encryptData, setEncryptData] = useState(true)
  const [encryptionPassword, setEncryptionPassword] = useState('')
  const [restoreFile, setRestoreFile] = useState(null)
  const [restorePassword, setRestorePassword] = useState('')
  const organizationId = useOrganizationId()
  const { isAdmin } = useOrganization()
  const { success, showError } = useToast()

  useEffect(() => {
    if (organizationId && isAdmin) {
      fetchBackupData()
    }
  }, [organizationId, isAdmin])

  const fetchBackupData = async () => {
    if (!organizationId) return
    try {
      setLoading(true)
      const [config, records] = await Promise.all([
        getBackupConfiguration(organizationId),
        getBackupRecords(organizationId),
      ])
      setBackupConfig(config)
      setBackupRecords(records)
    } catch (error) {
      console.error('Error fetching backup data:', error)
      showError('Failed to load backup data')
    } finally {
      setLoading(false)
    }
  }

  const handleBackup = async () => {
    if (!organizationId || !isAdmin) return

    if (encryptData && !encryptionPassword) {
      showError('Please enter an encryption password')
      return
    }

    try {
      setBackingUp(true)

      // Generate backup data
      let backupData = await generateBackupData(organizationId, includeAttachments)

      // Encrypt if requested
      if (encryptData && encryptionPassword) {
        const encryptionKey = generateEncryptionKey(organizationId, encryptionPassword)
        backupData = await encryptBackupData(backupData, encryptionKey)
        await storeEncryptionKey(organizationId, encryptionKey)
        success('Backup data encrypted successfully')
      }

      if (cloudService === 'local') {
        // Local download
        if (backupType === 'json') {
          const jsonString = JSON.stringify(backupData, null, 2)
          const blob = new Blob([jsonString], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `backup-${organizationId}-${Date.now()}.json`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
        } else {
          await downloadBackupExcel(organizationId, includeAttachments)
        }
        success('Backup downloaded successfully!')
      } else if (cloudService === 'google_drive') {
        await uploadBackupToGoogleDrive(organizationId, includeAttachments)
        success('Backup uploaded to Google Drive successfully!')
      } else if (cloudService === 'dropbox') {
        await uploadBackupToDropbox(organizationId, includeAttachments)
        success('Backup uploaded to Dropbox successfully!')
      } else if (cloudService === 's3') {
        await uploadBackupToS3(organizationId, includeAttachments)
        success('Backup uploaded to S3 successfully!')
      }

      // Log backup record
      await triggerManualBackup(organizationId)
      fetchBackupData()
      setEncryptionPassword('')
    } catch (error) {
      console.error('Error creating backup:', error)
      showError(error.message || 'Failed to create backup')
    } finally {
      setBackingUp(false)
    }
  }

  const handleRestore = async () => {
    if (!organizationId || !isAdmin || !restoreFile) return

    try {
      setRestoring(true)

      // Read file
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = reject
        reader.readAsText(restoreFile)
      })

      let backupData = JSON.parse(fileContent)

      // Decrypt if encrypted
      if (backupData.metadata?.encrypted) {
        if (!restorePassword) {
          showError('This backup is encrypted. Please enter the encryption password.')
          return
        }

        const encryptionKey = generateEncryptionKey(organizationId, restorePassword)
        backupData = await decryptBackupData(backupData, encryptionKey)
        success('Backup data decrypted successfully')
      }

      // TODO: Implement restore logic
      // This would restore data to the database
      // For now, just show success
      success('Backup restore initiated. This feature is under development.')
      
      setRestoreFile(null)
      setRestorePassword('')
    } catch (error) {
      console.error('Error restoring backup:', error)
      showError(error.message || 'Failed to restore backup')
    } finally {
      setRestoring(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-700'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-700'
      case 'failed':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  if (!isAdmin) {
    return (
      <div className="section-container">
        <Card variant="soft" className="p-6 text-center">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h2>
          <p className="text-gray-600">You need admin privileges to access backup and restore features.</p>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="section-container">
        <p className="text-gray-500">Loading backup configuration...</p>
      </div>
    )
  }

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
          <Database size={24} className="text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Backup & Restore</h1>
          <p className="text-gray-600">Create encrypted backups and restore your organization data</p>
        </div>
      </div>

      {/* Backup Configuration */}
      {backupConfig && (
        <Card variant="soft" className="p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Backup Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Frequency</p>
              <p className="font-semibold text-gray-900 capitalize">
                {backupConfig.backup_frequency || 'None'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Retention</p>
              <p className="font-semibold text-gray-900">{backupConfig.retention_days} days</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Backup</p>
              <p className="font-semibold text-gray-900">
                {backupConfig.last_backup_at ? formatDate(backupConfig.last_backup_at) : 'Never'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Create Backup */}
      <Card variant="soft" className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Download size={20} />
          Create Backup
        </h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Backup Format"
              options={[
                { value: 'json', label: 'JSON (Full Data)' },
                { value: 'excel', label: 'Excel (Spreadsheet)' },
              ]}
              value={backupType}
              onChange={(e) => setBackupType(e.target.value)}
            />
            <Select
              label="Storage Location"
              options={[
                { value: 'local', label: 'Local Download' },
                { value: 'google_drive', label: 'Google Drive' },
                { value: 'dropbox', label: 'Dropbox' },
                { value: 's3', label: 'AWS S3' },
              ]}
              value={cloudService}
              onChange={(e) => setCloudService(e.target.value)}
            />
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="includeAttachments"
                checked={includeAttachments}
                onChange={(e) => setIncludeAttachments(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600"
              />
              <label htmlFor="includeAttachments" className="text-sm text-gray-700">
                Include file attachments
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="encryptData"
                checked={encryptData}
                onChange={(e) => setEncryptData(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600"
              />
              <label htmlFor="encryptData" className="text-sm text-gray-700 flex items-center gap-2">
                <Lock size={16} />
                Encrypt sensitive data (Recommended)
              </label>
            </div>
            
            {encryptData && (
              <Input
                label="Encryption Password"
                type="password"
                value={encryptionPassword}
                onChange={(e) => setEncryptionPassword(e.target.value)}
                placeholder="Enter password to encrypt backup"
                className="mt-2"
              />
            )}
          </div>
          
          <Button
            onClick={handleBackup}
            disabled={backingUp || (encryptData && !encryptionPassword)}
            className="w-full"
          >
            {backingUp ? (
              <>
                <Clock size={18} className="mr-2 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download size={18} className="mr-2" />
                {encryptData ? 'Create Encrypted Backup' : 'Create Backup'}
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Restore Backup */}
      <Card variant="soft" className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Upload size={20} />
          Restore Backup
        </h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Backup File
            </label>
            <input
              type="file"
              accept=".json"
              onChange={(e) => setRestoreFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
          </div>
          
          <Input
            label="Encryption Password (if backup is encrypted)"
            type="password"
            value={restorePassword}
            onChange={(e) => setRestorePassword(e.target.value)}
            placeholder="Enter encryption password"
          />
          
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-yellow-900 mb-1">Warning</p>
                <p className="text-xs text-yellow-800">
                  Restoring a backup will replace existing data. Make sure you have a current backup before proceeding.
                </p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleRestore}
            disabled={restoring || !restoreFile}
            variant="secondary"
            className="w-full"
          >
            {restoring ? (
              <>
                <Clock size={18} className="mr-2 animate-spin" />
                Restoring...
              </>
            ) : (
              <>
                <Upload size={18} className="mr-2" />
                Restore Backup
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Backup History */}
      <Card variant="soft" className="p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Backup History</h4>
        {backupRecords.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No backup records</p>
        ) : (
          <div className="space-y-3">
            {backupRecords.map((record) => (
              <Card key={record.id} variant="soft" className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-gray-900 capitalize">
                        {record.backup_type} Backup
                      </span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {record.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {record.storage_location && (
                        <>
                          <strong>Location:</strong> {record.storage_location}
                          <br />
                        </>
                      )}
                      {record.backup_size_bytes && (
                        <>
                          <strong>Size:</strong> {(record.backup_size_bytes / 1024 / 1024).toFixed(2)} MB
                          <br />
                        </>
                      )}
                      <strong>Started:</strong> {formatDate(record.started_at)}
                      {record.completed_at && (
                        <>
                          <br />
                          <strong>Completed:</strong> {formatDate(record.completed_at)}
                        </>
                      )}
                    </p>
                    {record.error_message && (
                      <p className="text-xs text-red-600 mt-2">{record.error_message}</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

export default BackupRestore

